<template>
  <div class="package-tracking">
    <div class="package-tracking-intro">
      <h2 class="package-tracking-intro-title">Seguimiento de paquete</h2>
      <p class="package-tracking-intro-text">
        Consulta el estado de tus cupos y el avance por tipo de proceso.
        <span v-if="packages.length > 1" class="package-count">({{ packages.length }} paquetes)</span>
      </p>
    </div>

    <div v-if="loading" class="vx-card tracking-card">
      <div class="vx-card__collapsible-content tracking-card-inner">
        <div class="loading-state">
          <div class="loading-bar" />
          <span>Cargando información de los paquetes...</span>
        </div>
      </div>
    </div>

    <div v-else-if="errorMessage" class="vx-card tracking-card">
      <div class="tracking-card-inner">
        <div class="alert-box">{{ errorMessage }}</div>
      </div>
    </div>

    <div v-else-if="!packages.length" class="vx-card tracking-card">
      <div class="tracking-card-inner">
        <div class="empty-state">
          <h4>No tienes paquetes registrados</h4>
          <p>Cuando tu organización tenga paquetes asignados, aquí podrás ver su consumo y saldo disponible.</p>
        </div>
      </div>
    </div>

    <div v-else class="packages-accordion">
      <div
        v-for="summary in packages"
        :key="summary.package_id"
        class="vx-card package-package-card"
      >
        <button
          type="button"
          class="package-accordion-header"
          :aria-expanded="isOpen(summary.package_id)"
          @click="togglePackage(summary.package_id)"
        >
          <span class="package-accordion-title">{{ summary.package_name }}</span>
          <span
            class="package-accordion-badge"
            :class="statusClassFor(summary)"
          >
            {{ statusLabelFor(summary) }}
          </span>
          <span class="package-accordion-chevron" :class="{ 'is-open': isOpen(summary.package_id) }" />
        </button>

        <div
          v-show="isOpen(summary.package_id)"
          class="package-accordion-body"
        >
          <div class="headline-row">
            <div class="headline-item headline-main">
              <span class="headline-label">Paquete</span>
              <strong>{{ summary.package_name }}</strong>
              <small>Vigencia: {{ formatDate(summary.starts_on) }} a {{ formatDate(summary.ends_on) }}</small>
              <div class="package-state-row">
                <span class="headline-label package-state-label">Estado del paquete</span>
                <span
                  class="package-state-pill"
                  :class="packageStatePillClass(summary.status)"
                >{{ packageStateLabel(summary.status) }}</span>
              </div>
            </div>
            <div class="headline-item headline-kpi">
              <span class="headline-label">Total de cupos</span>
              <strong class="headline-value headline-value--total">{{ summary.total_contracted }}</strong>
            </div>
            <div class="headline-item headline-kpi">
              <span class="headline-label">Consumidos</span>
              <strong class="headline-value headline-value--consumed">{{ displayConsumed(summary.total_consumed) }}</strong>
            </div>
            <div class="headline-item headline-kpi">
              <span class="headline-label">Saldo disponible</span>
              <strong
                class="headline-value headline-value--balance"
                :class="balanceValueToneClass(summary)"
              >{{ summary.total_available }}</strong>
            </div>
          </div>

          <div class="package-pdf-row">
            <vx-tooltip
              text="Descargar la información de los procesos consumidos"
              position="top"
              color="primary"
            >
              <div class="package-pdf-inner">
                <vs-button
                  size="small"
                  type="border"
                  color="primary"
                  class="pdf-download-btn"
                  :disabled="pdfLoadingPackageId === summary.package_id"
                  @click="downloadPackageConsumptionPdf(summary)"
                >
                  <feather-icon
                    icon="DownloadIcon"
                    svg-classes="h-3 w-3 pdf-btn-icon"
                  />
                  <span class="pdf-btn-label">{{
                    pdfLoadingPackageId === summary.package_id ? 'Generando…' : 'Descargar PDF'
                  }}</span>
                </vs-button>
              </div>
            </vx-tooltip>
          </div>

          <!--
            REVERT diseño "consumo total": quitar la clase `package-consumption-total` del div siguiente
            y sustituir el interior por:
            <div class="progress-title-row">…</div> + <vs-progress :percent="…" color="primary" />
            (ver bloque PACKAGE_CONSUMPTION_TOTAL_DESIGN en <style>).
          -->
          <div class="progress-panel package-consumption-total">
            <div class="package-consumption-total__header">
              <div class="package-consumption-total__title-block">
                <span class="package-consumption-total__label">Consumo total del paquete</span>
                <span class="package-consumption-total__hint">Uso acumulado frente al cupo contratado</span>
              </div>
              <div
                class="package-consumption-total__percent-badge"
                :class="consumptionClassFor(summary)"
              >
                <span class="package-consumption-total__percent-num">{{ displayPercent(summary.consumed_percentage) }}</span>
                <span class="package-consumption-total__percent-sym">%</span>
              </div>
            </div>
            <div class="package-consumption-total__track">
              <vs-progress
                :percent="displayPercent(summary.consumed_percentage)"
                :color="consumptionProgressColor(summary)"
                :height="10"
                class="package-consumption-total__vs shadow-md"
              />
            </div>
          </div>

          <div class="table-wrapper">
            <table class="table package-table">
              <colgroup>
                <col class="col-process">
                <col class="col-num">
                <col class="col-num">
                <col class="col-num">
                <col class="col-num">
              </colgroup>
              <thead>
                <tr>
                  <th>Tipo de proceso</th>
                  <th class="text-center">Asignados</th>
                  <th class="text-center">Consumidos</th>
                  <th class="text-center">Disponibles</th>
                  <th class="text-center">% consumo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in summary.process_types" :key="`${summary.package_id}-${row.project_type_id}`">
                  <td>
                    <div class="process-name">
                      <strong>{{ projectTypeLabel(row.project_type_name) }}</strong>
                      <small>{{ displayConsumed(row.consumed) }} de {{ row.contracted }} cupos utilizados</small>
                    </div>
                  </td>
                  <td class="text-center cell-num">{{ row.contracted }}</td>
                  <td class="text-center cell-num">{{ displayConsumed(row.consumed) }}</td>
                  <td class="text-center cell-badge">
                    <span :class="availabilityClass(row.available)" class="availability-badge">
                      {{ row.available }}
                    </span>
                  </td>
                  <td class="text-center cell-badge">
                    <span class="consumption-chip">{{ displayPercent(row.consumed_percentage) }}%</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import api from '../../mixins/api'

export default {
  name: 'PackageTracking',
  mixins: [api],
  data () {
    return {
      loading: false,
      packages: [],
      openById: {},
      errorMessage: null,
      pdfLoadingPackageId: null,
      projectTypeLabels: {
        antecedentes: 'Antecedentes judiciales',
        antecedents: 'Antecedentes judiciales',
        risk_list: 'Listas de riesgo',
        academic_simple: 'Verificación académica',
        academic_antecedents: 'Académico y antecedentes',
        laboral: 'Referencias laborales',
        laboral_academic: 'Referencias laborales y académicas',
        references: 'Referencias personales y laborales',
        visit: 'Visita domiciliaria',
        basic_visit: 'Visita domiciliaria básica',
        special_visit: 'Visita domiciliaria especial',
        remote_work: 'Visita para teletrabajo',
        remote_work_simple: 'Visita de teletrabajo simple',
        remote_work_express: 'Visita de teletrabajo exprés',
        psychotechnical: 'Prueba psicotécnica',
        security: 'Estudio de seguridad',
        polygraph: 'Prueba de poligrafía',
        past_data: 'Validación Datacrédito',
        antecedentes_references: 'Antecedentes y referencias'
      }
    }
  },
  methods: {
    displayConsumed (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return 0
      return Math.max(0, Math.round(n))
    },
    displayPercent (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return 0
      return Math.min(100, Math.max(0, Math.round(n)))
    },
    projectTypeLabel (name) {
      if (!name) return 'Sin tipo'
      if (this.projectTypeLabels[name]) return this.projectTypeLabels[name]
      return name.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    },
    formatDate (value) {
      if (!value) return '-'
      const date = new Date(`${value}T00:00:00`)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('es-CO')
    },
    availabilityClass (value) {
      if (value <= 0) return 'availability-danger'
      if (value <= 2) return 'availability-warning'
      return 'availability-ok'
    },
    statusLabelFor (summary) {
      if (summary.total_available <= 0) return 'Sin cupos disponibles'
      return 'Con saldo'
    },
    statusClassFor (summary) {
      return summary.total_available <= 0 ? 'status-danger' : 'status-ok'
    },
    packageStateLabel (status) {
      const key = (status || '').toString().toLowerCase()
      const map = {
        draft: 'Borrador',
        active: 'Activo',
        cancelled: 'Cancelado'
      }
      return map[key] || (status ? String(status) : '—')
    },
    packageStatePillClass (status) {
      const key = (status || '').toString().toLowerCase()
      if (key === 'active') return 'package-state-pill--active'
      if (key === 'draft') return 'package-state-pill--draft'
      if (key === 'cancelled') return 'package-state-pill--cancelled'
      return 'package-state-pill--unknown'
    },
    balanceValueToneClass (summary) {
      const n = summary.total_available
      if (n <= 0) return 'headline-value--balance-zero'
      if (n <= 2) return 'headline-value--balance-low'
      return 'headline-value--balance-ok'
    },
    consumptionClassFor (summary) {
      const p = this.displayPercent(summary.consumed_percentage)
      if (p >= 95) return 'consumption-danger'
      if (p >= 80) return 'consumption-warning'
      return 'consumption-normal'
    },
    consumptionProgressColor (summary) {
      const p = this.displayPercent(summary.consumed_percentage)
      if (p >= 95) return 'danger'
      if (p >= 80) return 'warning'
      return 'primary'
    },
    isOpen (packageId) {
      return !!this.openById[packageId]
    },
    togglePackage (packageId) {
      const next = !this.openById[packageId]
      this.$set(this.openById, packageId, next)
    },
    initOpenState () {
      const next = {}
      this.packages.forEach((pkg, index) => {
        const id = pkg.package_id
        // Lista ordenada por más reciente primero: expandir el paquete más nuevo.
        next[id] = index === 0
      })
      this.openById = next
    },
    async parseApiErrorFromResponse (response) {
      if (!response || response.data == null) return null
      const d = response.data
      try {
        if (typeof d === 'string') {
          return JSON.parse(d)
        }
        if (typeof Blob !== 'undefined' && d instanceof Blob) {
          const text = await d.text()
          if (!text || !text.trim()) return null
          return JSON.parse(text)
        }
        if (typeof d === 'object') {
          return d
        }
      } catch (e) {
        return null
      }
      return null
    },
    async downloadPackageConsumptionPdf (summary) {
      const packageId = summary.package_id
      this.pdfLoadingPackageId = packageId
      try {
        const res = await axios({
          method: 'GET',
          url: `${this.url()}api/v1/clients/package_consumption_pdf.pdf`,
          params: {
            package_id: packageId,
            consumption_filter: 'package_validity'
          },
          headers: this.headers(),
          responseType: 'blob'
        })
        const blob = res.data
        const ct = (res.headers && res.headers['content-type']) || ''
        if (ct.includes('application/json')) {
          const text = await blob.text()
          const j = JSON.parse(text)
          throw new Error(j.message || j.error || 'No fue posible generar el PDF.')
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `paquete-${packageId}-procesos-consumidos.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        let msg = 'No fue posible descargar el PDF. Intenta de nuevo.'
        if (e.response) {
          const j = await this.parseApiErrorFromResponse(e.response)
          if (j) {
            msg = j.message || j.error || msg
          }
        } else if (e.message) {
          msg = e.message
        }
        this.$vs.notify({
          title: 'PDF',
          text: msg,
          color: 'danger',
          position: 'top-right'
        })
      } finally {
        this.pdfLoadingPackageId = null
      }
    },
    normalizePackagesPayload (data) {
      if (!data) return []
      if (Array.isArray(data.packages)) return data.packages
      if (data.package_id != null || data.package_name != null) return [data]
      return []
    },
    async loadPackageTracking () {
      this.loading = true
      this.errorMessage = null
      try {
        const result = await this.requestGetWithoutContent(`${this.url()}api/v1/clients/package_tracking`, {})
        this.packages = this.normalizePackagesPayload(result.data.data)
        this.initOpenState()
      } catch (e) {
        this.errorMessage = 'No fue posible cargar el seguimiento de paquete.'
        this.packages = []
      } finally {
        this.loading = false
      }
    }
  },
  created () {
    this.loadPackageTracking()
  }
}
</script>

<style scoped>
.package-tracking {
  margin-top: 4px;
}

/* Mismo lenguaje visual que Seguimiento rápido (Search.vue / .search-container) */
.package-tracking-intro {
  background: #f2edfc;
  border-radius: 16px;
  margin-bottom: 1.5rem;
  padding: 24px 18px;
}

/* Mismo tamaño que h2 global / Seguimiento rápido (no fijar font-size menor que el tema) */
.package-tracking-intro-title {
  color: #5f008f;
  font-family: Montserrat, sans-serif;
  font-style: normal;
  font-weight: 800;
  line-height: 1.25;
  margin: 0;
}

.package-tracking-intro-text {
  color: #3c1a4e;
  font-family: Montserrat, sans-serif;
  font-size: 0.95rem;
  font-style: normal;
  font-weight: 400;
  margin: 10px 0 0;
}

.tracking-card-inner {
  padding: 20px 22px 24px;
}

.tracking-card {
  border-radius: 14px;
}

.packages-accordion {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.package-package-card {
  border-radius: 14px;
  overflow: hidden;
}

.package-count {
  color: #5f008f;
  font-weight: 600;
}

.package-accordion-header {
  align-items: center;
  background: #faf7fd;
  border: none;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  justify-content: flex-start;
  padding: 16px 20px;
  text-align: left;
  width: 100%;
}

.package-accordion-header:hover {
  background: #f3ebfb;
}

.package-accordion-title {
  color: #5f008f;
  flex: 1;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.25;
  min-width: 0;
}

.package-accordion-badge {
  border-radius: 999px;
  flex-shrink: 0;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 14px;
}

.package-accordion-chevron {
  border-right: 2px solid #5f008f;
  border-bottom: 2px solid #5f008f;
  display: inline-block;
  height: 10px;
  margin-left: auto;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
  width: 10px;
}

.package-accordion-chevron.is-open {
  transform: rotate(-135deg);
}

.package-accordion-body {
  background: #fff;
  border-top: 1px solid #ece4f4;
  padding: 22px 20px 24px;
}

.package-pdf-row {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  margin-top: 10px;
  min-height: 0;
  padding: 0;
}

.package-pdf-inner {
  display: inline-block;
  line-height: 1;
  max-width: 100%;
}

.loading-state {
  color: #5f008f;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 4px;
}

.loading-bar {
  background: linear-gradient(90deg, #cba0e4 0%, #5f008f 100%);
  border-radius: 999px;
  height: 8px;
  width: 120px;
}

.headline-row {
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.headline-item {
  background: #faf7fd;
  border: 1px solid #f0e6f8;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  flex: 1 1 140px;
  gap: 2px;
  min-width: 0;
  padding: 12px;
}

.headline-main {
  flex: 1.25 1 200px;
}

.headline-kpi {
  align-items: center;
  flex: 1 1 120px;
  flex-direction: row;
  gap: 12px;
  justify-content: space-between;
}

.headline-item.headline-main {
  background: linear-gradient(120deg, #f7f2fc 0%, #f3e8fb 100%);
}

.package-state-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-top: 10px;
}

.package-state-label {
  letter-spacing: 0.02em;
  margin: 0;
  text-transform: uppercase;
}

.package-state-pill {
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 5px 12px;
}

.package-state-pill--active {
  background: #e8f7ef;
  color: #0e8a46;
}

.package-state-pill--draft {
  background: #ede6f7;
  color: #5f008f;
}

.package-state-pill--cancelled {
  background: #fdecec;
  color: #b42318;
}

.package-state-pill--unknown {
  background: #f0eef5;
  color: #4f3f5f;
}

.headline-label {
  color: #7a6792;
  font-size: 0.76rem;
  text-transform: uppercase;
}

.headline-kpi .headline-label {
  flex: 1;
  font-size: 0.82rem;
  text-align: left;
}

.headline-value {
  font-size: 1.95rem;
  font-weight: 700;
  line-height: 1.15;
  text-align: right;
}

.headline-kpi .headline-value--total {
  color: #5f008f;
}

.headline-kpi .headline-value--consumed {
  color: #8428b8;
}

.headline-kpi .headline-value--balance {
  color: #5f008f;
}

.headline-kpi .headline-value--balance.headline-value--balance-ok {
  color: #0e8a46;
}

.headline-kpi .headline-value--balance.headline-value--balance-low {
  color: #b54708;
}

.headline-kpi .headline-value--balance.headline-value--balance-zero {
  color: #b42318;
}

.headline-item strong:not(.headline-value) {
  color: #34114d;
  font-size: 1.1rem;
}

.headline-item small {
  color: #6b5b7d;
}

.progress-panel {
  background: #fcfbfe;
  border: 1px solid #eee4f6;
  border-radius: 12px;
  margin-top: 14px;
  padding: 12px;
}

/* ---------------------------------------------------------------------------
   PACKAGE_CONSUMPTION_TOTAL_DESIGN — diseño mejorado del % de consumo total.
   Para revertir: borrar desde esta línea hasta el comentario FIN_PACKAGE_CONSUMPTION_TOTAL_DESIGN
   y en el template quitar `package-consumption-total` + restaurar el bloque simple (ver comentario HTML).
   --------------------------------------------------------------------------- */
.progress-panel.package-consumption-total {
  background: linear-gradient(165deg, #ffffff 0%, #faf7fd 42%, #f3ebfb 100%);
  border: 1px solid #e4d4f2;
  border-radius: 14px;
  box-shadow: 0 2px 14px rgba(95, 0, 143, 0.07);
  padding: 16px 18px 18px;
}

.package-consumption-total__header {
  align-items: flex-start;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.package-consumption-total__title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.package-consumption-total__label {
  color: #34114d;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.package-consumption-total__hint {
  color: #7b6c89;
  font-size: 0.78rem;
  font-weight: 400;
  line-height: 1.35;
}

.package-consumption-total__percent-badge {
  border-radius: 12px;
  flex-shrink: 0;
  line-height: 1;
  min-width: 4.25rem;
  padding: 10px 14px;
  text-align: center;
}

.package-consumption-total__percent-badge.consumption-normal {
  background: linear-gradient(160deg, #faf5ff 0%, #f3e8fb 100%);
  border: 1px solid #ddc6ee;
  box-shadow: 0 1px 6px rgba(95, 0, 143, 0.08);
}

.package-consumption-total__percent-badge.consumption-warning {
  background: linear-gradient(160deg, #fffbf5 0%, #fff4df 100%);
  border: 1px solid #f0d4a8;
  box-shadow: 0 1px 6px rgba(181, 71, 8, 0.12);
}

.package-consumption-total__percent-badge.consumption-danger {
  background: linear-gradient(160deg, #fff8f8 0%, #fdecec 100%);
  border: 1px solid #f0c4c4;
  box-shadow: 0 1px 6px rgba(180, 35, 24, 0.1);
}

.package-consumption-total__percent-num {
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.package-consumption-total__percent-sym {
  font-size: 0.95rem;
  font-weight: 700;
  margin-left: 1px;
  opacity: 0.92;
}

.package-consumption-total__percent-badge.consumption-normal .package-consumption-total__percent-num,
.package-consumption-total__percent-badge.consumption-normal .package-consumption-total__percent-sym {
  color: #5f008f;
}

.package-consumption-total__percent-badge.consumption-warning .package-consumption-total__percent-num,
.package-consumption-total__percent-badge.consumption-warning .package-consumption-total__percent-sym {
  color: #b54708;
}

.package-consumption-total__percent-badge.consumption-danger .package-consumption-total__percent-num,
.package-consumption-total__percent-badge.consumption-danger .package-consumption-total__percent-sym {
  color: #b42318;
}

.package-consumption-total__track {
  position: relative;
}

.package-consumption-total__track ::v-deep .vs-progress--background {
  border-radius: 999px;
  overflow: hidden;
}

.package-consumption-total__track ::v-deep .vs-progress--foreground {
  border-radius: 999px;
}

/* FIN_PACKAGE_CONSUMPTION_TOTAL_DESIGN */

.pdf-download-btn {
  align-items: center;
  border-color: #5f008f !important;
  color: #5f008f !important;
  display: inline-flex;
  font-size: 0.8125rem !important;
  gap: 6px;
  justify-content: center;
  line-height: 1.2 !important;
  max-width: 100%;
  min-height: 0 !important;
  padding: 0.3rem 0.65rem !important;
  white-space: nowrap;
}

/* Vuesax aplica padding alto al botón; lo compactamos en esta fila */
.package-pdf-row ::v-deep .vs-button {
  min-height: 30px !important;
  padding: 0.3rem 0.65rem !important;
}

.package-pdf-row ::v-deep .vs-button--small {
  padding: 0.28rem 0.6rem !important;
}

.pdf-btn-icon {
  flex-shrink: 0;
  stroke: #5f008f;
}

.pdf-btn-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.consumption-normal {
  color: #5f008f;
}

.consumption-warning {
  color: #b54708;
}

.consumption-danger {
  color: #b42318;
}

.table-wrapper {
  margin-top: 14px;
  width: 100%;
}

.package-table {
  margin-bottom: 0;
  table-layout: fixed;
  width: 100%;
}

.package-table col.col-process {
  width: 36%;
}

.package-table col.col-num {
  width: 16%;
}

.package-table thead th {
  color: #5f008f;
  font-weight: 700;
  font-size: 0.88rem;
  padding: 10px 8px;
  vertical-align: middle;
}

.package-table tbody td {
  padding: 12px 8px;
  vertical-align: middle;
  word-wrap: break-word;
}

.cell-num {
  color: #34114d;
  font-size: 1.05rem;
  font-weight: 600;
}

.cell-badge {
  vertical-align: middle;
}

.process-name {
  display: flex;
  flex-direction: column;
}

.process-name strong {
  color: #3c1a4e;
}

.process-name small {
  color: #7b6c89;
}

.availability-badge {
  border-radius: 999px;
  display: inline-block;
  font-weight: 700;
  min-width: 34px;
  padding: 4px 9px;
}

.availability-ok {
  background: #e8f7ef;
  color: #0e8a46;
}

.availability-warning {
  background: #fff4df;
  color: #b54708;
}

.availability-danger {
  background: #fdecec;
  color: #b42318;
}

.consumption-chip {
  background: #f3e7fb;
  border-radius: 999px;
  color: #5f008f;
  display: inline-block;
  font-weight: 700;
  padding: 4px 9px;
}

.alert-box {
  background: #fdecea;
  border-left: 4px solid #b42318;
  border-radius: 8px;
  color: #7f1d1d;
  padding: 12px;
}

.empty-state {
  background: linear-gradient(120deg, #fbf8fe 0%, #f5edf9 100%);
  border: 1px dashed #ddc6ee;
  border-radius: 12px;
  color: #3c1a4e;
  padding: 18px;
}

.empty-state h4 {
  color: #5f008f;
  margin-bottom: 6px;
}

.empty-state p {
  margin: 0;
}

.status-ok {
  background: #e8f7ef;
  color: #0e8a46;
}

.status-danger {
  background: #fdecec;
  color: #b42318;
}

@media (max-width: 640px) {
  .tracking-card-inner {
    padding: 14px 12px 18px;
  }

  .package-tracking-intro {
    padding: 18px 14px;
  }
}
</style>
